/**
 * Shared conversion from OSM/Overpass data to CafePointFeature.
 * Used by r2-data.ts (Overpass-style elements).
 */

import type {
  Feature,
  Geometry,
  LineString,
  MultiLineString,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from "geojson";
import type { CafeFeatureProperties, CafePointFeature, OverpassElement, PlaceCategory } from "./types";

function centroidFromRing(ring: Position[]): Position {
  let sumX = 0;
  let sumY = 0;
  for (const point of ring) {
    const x = point[0] ?? 0;
    const y = point[1] ?? 0;
    sumX += x;
    sumY += y;
  }
  if (ring.length === 0) return [0, 0];
  return [sumX / ring.length, sumY / ring.length];
}

function centroidFromPolygon(coords: Position[][]): Position {
  return centroidFromRing(coords[0] ?? []);
}

function centroidFromMultiPolygon(coords: Position[][][]): Position {
  return centroidFromPolygon(coords[0] ?? []);
}

function midpointFromLine(coords: Position[]): Position {
  if (coords.length === 0) return [0, 0];
  return coords[Math.floor(coords.length / 2)] ?? [0, 0];
}

function midpointFromMultiLine(coords: Position[][]): Position {
  return midpointFromLine(coords[0] ?? []);
}

function getTags(feature: Feature<Geometry>): Record<string, string | undefined> {
  const rawProps = feature.properties ?? {};
  return (rawProps.tags ?? rawProps) as Record<string, string | undefined>;
}

function buildAddressFromTags(tags: Record<string, string | undefined>): string | undefined {
  const street = tags["addr:street"];
  const houseNumber = tags["addr:housenumber"];
  const postcode = tags["addr:postcode"];
  const city = tags["addr:city"];
  const line1 = [street, houseNumber].filter(Boolean).join(" ").trim();
  const line2 = [postcode, city].filter(Boolean).join(" ").trim();
  const address = [line1, line2].filter(Boolean).join(", ").trim();
  return address || undefined;
}

function getCountryCodeFromTags(tags: Record<string, string | undefined>): string | undefined {
  const rawValue =
    tags["addr:country"] ??
    tags["ISO3166-1:alpha2"] ??
    tags["iso3166-1:alpha2"] ??
    tags["country_code"] ??
    tags["is_in:country_code"];
  if (!rawValue) return undefined;
  const normalized = rawValue.trim();
  if (!/^[a-z]{2}$/i.test(normalized)) return undefined;
  return normalized.toLowerCase();
}

function getStateFromTags(tags: Record<string, string | undefined>): string | undefined {
  const rawValue = tags["addr:state"] ?? tags["is_in:state"] ?? tags["addr:province"] ?? tags["state"];
  const normalized = rawValue?.trim();
  return normalized || undefined;
}

export function getOsmRef(feature: Feature<Geometry>): {
  osmType: "node" | "way" | "relation";
  osmId: number;
} {
  const featureId = feature.id;
  let osmType: "node" | "way" | "relation" = "node";
  if (typeof featureId === "string") {
    if (featureId.startsWith("way/")) osmType = "way";
    else if (featureId.startsWith("relation/")) osmType = "relation";
  }
  let osmId = 0;
  if (typeof featureId === "number") {
    osmId = featureId;
  } else if (typeof featureId === "string") {
    const parsed = Number.parseInt(featureId.split("/")[1] ?? "0", 10);
    if (Number.isFinite(parsed)) osmId = parsed;
  }
  return { osmType, osmId };
}

export function toPointGeometry(geometry: Geometry): Point | null {
  if (geometry.type === "Point") return geometry;
  if (geometry.type === "Polygon") {
    return { type: "Point", coordinates: centroidFromPolygon((geometry as Polygon).coordinates) };
  }
  if (geometry.type === "MultiPolygon") {
    return { type: "Point", coordinates: centroidFromMultiPolygon((geometry as MultiPolygon).coordinates) };
  }
  if (geometry.type === "LineString") {
    return { type: "Point", coordinates: midpointFromLine((geometry as LineString).coordinates) };
  }
  if (geometry.type === "MultiLineString") {
    return {
      type: "Point",
      coordinates: midpointFromMultiLine((geometry as MultiLineString).coordinates),
    };
  }
  return null;
}

function detectCategory(tags: Record<string, string | undefined>): PlaceCategory | null {
  if (tags.amenity === "cafe" || tags.leisure === "outdoor_seating") return "cafe";
  if (tags.amenity === "bar" || tags.amenity === "pub") return "bar";
  if (tags.amenity === "restaurant") return "restaurant";
  return null;
}

/**
 * Convert a GeoJSON Feature (e.g. from R2) to CafePointFeature.
 * Returns null if the feature is not a supported POI type (cafe, bar, pub, outdoor_seating).
 */
export function toCafePointFeature(feature: Feature<Geometry>): CafePointFeature | null {
  const geometry = feature.geometry;
  if (!geometry) return null;

  const tags = getTags(feature);
  const category = detectCategory(tags);
  if (!category) return null;

  const pointGeometry = toPointGeometry(geometry);
  if (!pointGeometry) return null;

  const { osmId, osmType } = getOsmRef(feature);
  const properties: CafeFeatureProperties = {
    osmId,
    osmType,
    category,
    name: tags.name,
    address: buildAddressFromTags(tags),
    openingHours: tags.opening_hours,
    outdoorSeating: tags.outdoor_seating,
    countryCode: getCountryCodeFromTags(tags),
    state: getStateFromTags(tags),
  };

  return {
    type: "Feature",
    geometry: pointGeometry,
    properties,
  };
}

/**
 * Convert an Overpass-style element (e.g. from R2) to a GeoJSON Feature so toCafePointFeature can process it.
 */
export function overpassElementToGeoJSONFeature(element: OverpassElement): Feature<Point> | null {
  const lat = element.lat;
  const lon = element.lon;
  if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }
  const osmType = element.type ?? "node";
  if (osmType !== "node" && osmType !== "way" && osmType !== "relation") {
    return null;
  }
  return {
    type: "Feature",
    id: `${osmType}/${element.id}`,
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: { tags: element.tags ?? {} },
  };
}
